import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { db } from "../api/firebase";
import {
  getJourneyBySlug,
  saveEcardDraft,
  loadEcardDraft,
} from "../constants/giftJourney";
import { getGiftCardSummaryById } from "../services/giftCardSummaryService";
import { mergeSummaryWithVisualPreset } from "../constants/ecardVisualPresets";
import "../styles/productDetails.css";
import "../styles/journey.css";

const emptyForm = {
  cardTitle: "",
  recipientName: "",
  senderName: "",
  greetingLine: "",
  mainMessage: "",
};

export default function ProductCustomize() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const occasionParam = searchParams.get("occasion") || "egift";
  const journey = getJourneyBySlug(occasionParam);
  const mode = searchParams.get("mode") || "instant";

  const [product, setProduct] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formErr, setFormErr] = useState("");

  useEffect(() => {
    if (!db) {
      setLoadErr("Service is temporarily unavailable. Please try again shortly.");
      return;
    }
    let cancelled = false;
    setLoadErr(null);
    setProduct(null);
    (async () => {
      const summary = await getGiftCardSummaryById(id);
      if (cancelled) return;
      if (!summary) {
        setLoadErr("Gift card not found in catalog.");
        return;
      }
      const merged = mergeSummaryWithVisualPreset(summary, occasionParam);
      setProduct(merged);
      const existing = loadEcardDraft(id);
      setForm({
        ...emptyForm,
        cardTitle:     existing?.cardTitle     || merged.Tittle || "",
        recipientName: existing?.recipientName || "",
        senderName:    existing?.senderName    || "",
        greetingLine:  existing?.greetingLine  || "",
        mainMessage:   existing?.mainMessage   || "",
      });
    })();
    return () => { cancelled = true; };
  }, [id, occasionParam]);

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const handleContinue = (e) => {
    e.preventDefault();
    setFormErr("");
    if (!form.recipientName.trim() || !form.senderName.trim() || !form.mainMessage.trim()) {
      setFormErr("Please fill recipient name, your name, and the main message.");
      return;
    }
    saveEcardDraft(id, {
      ...form,
      occasionSlug:        journey.slug,
      giftkartCategoryId:  journey.giftkartCategoryId,
      formatId:            product.formatId,
    });
    if (mode === "schedule") {
      navigate(`/product/${id}/schedule?occasion=${encodeURIComponent(journey.slug)}`);
      return;
    }
    navigate(`/product/${id}/account`);
  };

  if (loadErr) {
    return (
      <div className="pd-wrap">
        <div className="pd-error journey-panel">{loadErr}</div>
        <Link
          to={`/product/${id}?occasion=${encodeURIComponent(occasionParam)}`}
          className="journey-link-back"
        >
          ← Back to design
        </Link>
      </div>
    );
  }

  if (!product) return <p className="pd-loading">Loading…</p>;

  return (
    <div className="pd-wrap">
      <div className="journey-panel journey-customize journey-customize--form-only">
        <div className="journey-customize-head">
          <Link
            to={`/product/${id}?occasion=${encodeURIComponent(journey.slug)}`}
            className="journey-link-back"
          >
            ← Back to product
          </Link>
          <span className="journey-pill">{journey.label}</span>
          <h1>Personalize your E-Card</h1>
          <p>These details appear on the final animated card your recipient opens. After payment you will get a link to copy and share.</p>
        </div>

        <form className="journey-form journey-form--wide" onSubmit={handleContinue}>
          <label className="journey-field">
            <span>Card title</span>
            <input
              value={form.cardTitle}
              onChange={(e) => update("cardTitle", e.target.value)}
              placeholder="e.g. Happy Birthday, Priya!"
            />
          </label>

          <label className="journey-field">
            <span>To (recipient name) *</span>
            <input
              value={form.recipientName}
              onChange={(e) => update("recipientName", e.target.value)}
              placeholder="Who is this for?"
              required
            />
          </label>

          <label className="journey-field">
            <span>From (your name) *</span>
            <input
              value={form.senderName}
              onChange={(e) => update("senderName", e.target.value)}
              placeholder="How you sign the card"
              required
            />
          </label>

          <label className="journey-field">
            <span>Short greeting</span>
            <input
              value={form.greetingLine}
              onChange={(e) => update("greetingLine", e.target.value)}
              placeholder="e.g. With love on your special day"
            />
          </label>

          <label className="journey-field">
            <span>Main message *</span>
            <textarea
              value={form.mainMessage}
              onChange={(e) => update("mainMessage", e.target.value)}
              rows={5}
              placeholder="Write the message they will read inside the card…"
              required
            />
          </label>

          {formErr && <p className="pd-error journey-inline-err">{formErr}</p>}

          <div className="journey-form-actions pd-customize-actions">
            <button type="submit" className="pd-btn pd-btn-pay">
              Continue to payment →
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
